/**
 * Credentials Manager - Alpine.js component for managing connection credentials
 *
 * This component handles:
 * - Loading and displaying credentials list
 * - Add/Edit modal with form state
 * - Verification flow before saving
 * - CRUD operations via the credentials API
 */

import { verifyCredential } from "@/application/components/admin/connections/connectionVerification";

/**
 * @param {Object} config
 * @param {number|null} config.connectionId - The connection ID (null for new connections)
 * @param {string} config.connectionType - The connection type (e.g., 'github', 'bitbucket')
 * @param {boolean} config.isNewConnection - Whether this is a new connection being created
 * @param {string} config.baseUrl - Base URL for the connections API
 * @param {Array} config.verificationSteps - Steps for credential verification
 * @param {string} config.csrfToken - CSRF token for requests
 */
export function credentialsManager({
  connectionId,
  connectionType,
  isNewConnection,
  baseUrl,
  verificationSteps = [],
  csrfToken,
  supportsExpiration = false,
}) {
  return {
    // Connection context
    connectionId,
    connectionType,
    supportsExpiration,
    isNewConnection,
    baseUrl,
    verificationSteps,
    csrfToken,

    // Credentials list state
    credentials: [],
    loading: false,
    loadError: null,

    // Modal state
    showModal: false,
    isEditing: false,
    editingCredentialId: null,
    modalTitle: "Add Credential",

    // Form state
    formData: {
      label: "",
      settings: {},
      secrets: {},
    },
    initialFormData: null,

    // Submission state
    submitting: false,
    error: null,
    verificationStep: null,
    copied: false,
    copiedWithSecret: false,

    // Delete state
    deleting: null,

    // Unsaved changes warning
    showUnsavedWarning: false,

    /**
     * Initialize the component
     */
    async init() {
      if (this.connectionId) {
        await this.fetchCredentials();
      }

      this.handleModalBeforeHide = this.handleModalBeforeHide.bind(this);

      // Listen for modal close attempts
      window.addEventListener("modal:before-hide", this.handleModalBeforeHide);
    },

    /**
     * Clean up event listeners
     */
    destroy() {
      window.removeEventListener(
        "modal:before-hide",
        this.handleModalBeforeHide,
      );
    },

    /**
     * Handle connection created event (for create flow)
     */
    onConnectionCreated(detail) {
      if (detail.connectionId) {
        this.connectionId = detail.connectionId;
        this.isNewConnection = false;
        this.fetchCredentials();
      }
    },

    // ==================== API Methods ====================

    /**
     * Fetch credentials list from API
     */
    async fetchCredentials() {
      if (!this.connectionId) return;

      this.loading = true;
      this.loadError = null;

      try {
        const response = await fetch(
          `${this.baseUrl}/${this.connectionId}/credentials`,
          {
            headers: { Accept: "application/json" },
          },
        );

        const data = await response.json();

        if (response.ok && data.ok !== false) {
          this.credentials = data.credentials || [];
        } else {
          this.loadError =
            data.message || data.error || "Failed to load credentials";
        }
      } catch (e) {
        console.error("Failed to fetch credentials:", e);
        this.loadError = "Failed to load credentials";
      } finally {
        this.loading = false;
      }
    },

    /**
     * Create a new credential
     */
    async createCredential(formData) {
      const response = await fetch(
        `${this.baseUrl}/${this.connectionId}/credentials`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: formData,
        },
      );

      const data = await response.json();

      if (!response.ok || data.ok === false) {
        throw new Error(
          data.display_message || data.message || "Failed to create credential",
        );
      }

      return data;
    },

    /**
     * Update an existing credential
     */
    async updateCredential(credentialId, formData) {
      const response = await fetch(
        `${this.baseUrl}/${this.connectionId}/credentials/${credentialId}`,
        {
          method: "PATCH",
          headers: {
            Accept: "application/json",
          },
          body: formData,
        },
      );

      const data = await response.json();

      if (!response.ok || data.ok === false) {
        throw new Error(
          data.display_message || data.message || "Failed to update credential",
        );
      }

      return data;
    },

    /**
     * Delete a credential
     */
    async deleteCredential(credentialId) {
      const response = await fetch(
        `${this.baseUrl}/${this.connectionId}/credentials/${credentialId}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": this.csrfToken,
          },
        },
      );

      const data = await response.json();

      if (!response.ok || data.ok === false) {
        throw new Error(
          data.display_message || data.message || "Failed to delete credential",
        );
      }

      return data;
    },

    // ==================== Modal Methods ====================

    /**
     * Open modal to add a new credential
     */
    openAddModal() {
      this.isEditing = false;
      this.editingCredentialId = null;
      this.modalTitle = "Add Credential";
      this.formData = {
        label: "",
        settings: {},
        secrets: {},
      };
      this.initialFormData = JSON.stringify(this.formData);
      this.error = null;
      this.verificationStep = null;
      this.copied = false;
      this.copiedWithSecret = false;
      this.showUnsavedWarning = false;
      this.showModal = true;
    },

    /**
     * Open modal to edit an existing credential
     */
    openEditModal(credential) {
      this.isEditing = true;
      this.editingCredentialId = credential.id;
      this.modalTitle = `Edit Credential${credential.label ? `: ${credential.label}` : ""}`;

      // Convert ISO date to YYYY-MM-DD for date input
      let expirationDate = "";
      if (credential.expiration_date) {
        expirationDate = credential.expiration_date.split("T")[0];
      }

      this.formData = {
        label: credential.label || "",
        expiration_date: expirationDate,
        settings: { ...(credential.settings || {}) },
        secrets: {}, // Never pre-fill secrets
      };
      this.initialFormData = JSON.stringify(this.formData);
      this.error = null;
      this.verificationStep = null;
      this.copied = false;
      this.copiedWithSecret = false;
      this.showUnsavedWarning = false;
      this.showModal = true;
    },

    /**
     * Close the modal (with unsaved changes check)
     */
    closeModal() {
      if (this.hasUnsavedChanges() && !this.showUnsavedWarning) {
        this.showUnsavedWarning = true;
        return;
      }
      this.forceCloseModal();
    },

    /**
     * Force close the modal without checking for unsaved changes
     */
    forceCloseModal() {
      this.showModal = false;
      this.showUnsavedWarning = false;
      this.resetFormState();
    },

    /**
     * Handle modal before hide event
     */
    handleModalBeforeHide(event) {
      if (!this.showModal) return;

      if (this.hasUnsavedChanges() && !this.showUnsavedWarning) {
        event.preventDefault();
        this.showUnsavedWarning = true;
      }
    },

    /**
     * Check if form has unsaved changes
     */
    hasUnsavedChanges() {
      if (!this.initialFormData) return false;
      return JSON.stringify(this.formData) !== this.initialFormData;
    },

    /**
     * Reset form state
     */
    resetFormState() {
      this.formData = { label: "", settings: {}, secrets: {} };
      this.initialFormData = null;
      this.error = null;
      this.verificationStep = null;
      this.submitting = false;
      this.copied = false;
      this.copiedWithSecret = false;
    },

    // ==================== Form Submission ====================

    /**
     * Handle form submission - verify then save
     */
    async onSubmit() {
      this.submitting = true;
      this.error = null;

      try {
        // Step 1: Build form data from the actual form element
        const formElement = this.$refs.credentialForm;
        const formData = new FormData(formElement);

        // Step 2: Run verification
        const verified = await this.runVerification(formData);
        if (!verified) {
          this.submitting = false;
          return;
        }

        // Step 3: Save the credential
        this.verificationStep = {
          current: this.verificationSteps.length + 1,
          total: this.verificationSteps.length + 1,
          label: this.isEditing
            ? "Updating credential..."
            : "Adding credential...",
        };

        if (this.isEditing) {
          await this.updateCredential(this.editingCredentialId, formData);
        } else {
          await this.createCredential(formData);
        }

        // Success!
        await this.fetchCredentials();
        this.forceCloseModal();

        window.pushNotification?.({
          title: this.isEditing ? "Credential Updated" : "Credential Added",
        });
      } catch (e) {
        this.error = { message: e.message || "An unexpected error occurred" };
      } finally {
        this.submitting = false;
        this.verificationStep = null;
      }
    },

    /**
     * Run step-by-step verification
     */
    async runVerification(formData) {
      const steps = this.verificationSteps;
      const totalSteps = steps.length;

      for (let i = 0; i < totalSteps; i++) {
        const step = i + 1;
        const stepConfig = steps[i];

        this.verificationStep = {
          current: step,
          total: totalSteps + 1, // +1 for the save step
          label: stepConfig.label || `Verifying (${step}/${totalSteps})...`,
        };

        const result = await verifyCredential({
          connectionId: this.connectionId,
          credentialId: this.isEditing ? this.editingCredentialId : null,
          verificationStep: step,
          formData,
        });

        if (!result.success) {
          this.error = {
            message: result.error?.errorMessage || "Verification failed",
            errorLink: result.error?.errorLink,
            curlCommand: result.error?.curlCommand,
            tokenForCurl: result.error?.tokenForCurl,
            rawMessage: result.error?.rawMessage,
          };
          return false;
        }
      }

      return true;
    },

    // ==================== Delete ====================

    /**
     * Confirm and delete a credential
     */
    async confirmDelete(credential) {
      const label = credential.label || "this credential";
      if (
        !confirm(
          `Are you sure you want to delete "${label}"? This action cannot be undone.`,
        )
      ) {
        return;
      }

      this.deleting = credential.id;

      try {
        await this.deleteCredential(credential.id);
        await this.fetchCredentials();

        window.pushNotification?.({
          title: "Credential Deleted",
        });
      } catch (e) {
        alert(e.message || "Failed to delete credential");
      } finally {
        this.deleting = null;
      }
    },

    // ==================== Utilities ====================

    /**
     * Format a date string for display
     */
    formatDate(dateString) {
      if (!dateString) return "";
      const d = new Date(dateString);
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    },

    /**
     * Calculate days until a date
     */
    daysUntilExpiration(dateString) {
      if (!dateString) return Infinity;
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const expDate = new Date(dateString);
      expDate.setHours(0, 0, 0, 0);
      return Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
    },

    /**
     * Copy curl command to clipboard (redacted version)
     */
    copyCurlCommand() {
      if (!this.error?.curlCommand) return;

      navigator.clipboard.writeText(this.error.curlCommand);
      this.copied = true;
      setTimeout(() => (this.copied = false), 2000);
    },

    /**
     * Copy curl command with secrets to clipboard
     */
    copyCurlWithSecret() {
      if (!this.error?.curlCommand) return;

      let command = this.error.curlCommand;
      if (this.error.tokenForCurl) {
        command = command.replace(
          /REDACTED_.*(TOKEN|PASSWORD|KEY|SECRET)/g,
          this.error.tokenForCurl,
        );
      }

      navigator.clipboard.writeText(command);
      this.copiedWithSecret = true;
      setTimeout(() => (this.copiedWithSecret = false), 2000);
    },
  };
}
